import { useState, useEffect } from 'react';
import { useDropzone, ErrorCode } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Button } from '@material-ui/core';
import { FilesList, Loader } from 'components';

import {
  useNotification,
  NotificationProvider,
} from 'shared/utils/Notification';
import { ThemeProvider, theme } from 'configs/theme-configurator';

import { useStyles } from './style';

export type DropzoneClasses = Partial<{
  container: string;
  root: string;
}>;

interface Props {
  classes?: DropzoneClasses;
  disabled?: boolean;
  uploadFiles?: (files: File[]) => Promise<void>;
  files?: File[];
  handleChange?: (files: File[]) => void;
  handleRemoveFile?: (name: string) => void;
}

const Dropzone = ({
  classes: propClasses,
  handleChange,
  files,
  handleRemoveFile,
  uploadFiles,
  disabled,
}: Props) => {
  const classes = useStyles();
  const notification = useNotification();
  const { t } = useTranslation();
  const [upload, setUpload] = useState(false);
  const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
    disabled,
    noClick: true,
    noKeyboard: true,
    validator: (file) => {
      if (file.size > 0) return null;

      const message = `Tiedosto (${file.name}) on tyhjä eikä sitä voida ladata.`;

      notification.open({
        description: message,
        type: 'warning',
      });

      return {
        code: ErrorCode.FileTooSmall,
        message,
      };
    },
  });

  useEffect(() => {
    if (uploadFiles && acceptedFiles.length) {
      (async function () {
        setUpload(true);

        await uploadFiles(acceptedFiles);

        setUpload(false);
      })();
    }

    /* eslint-disable-next-line */
  }, [acceptedFiles]);

  useEffect(() => {
    if (acceptedFiles.length && handleChange) {
      handleChange(acceptedFiles);
    }
  }, [acceptedFiles, handleChange]);

  if (upload) {
    return (
      <div className={classes.container}>
        <div className={classes.root}>
          <Loader themeColor="darkBlue" />
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(classes.container, propClasses?.container)}>
      <div
        className={clsx(classes.root, propClasses?.root)}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <span className={classes.text}>{t('DocDrop')}</span>
        <span className={classes.text}>{t('Or')}</span>
        <Button type="button" classes={{ root: classes.btn }} onClick={open}>
          {t('Browse')}
        </Button>
      </div>
      {files && files.length ? (
        <FilesList files={files} removeFile={handleRemoveFile} />
      ) : null}
    </div>
  );
};

const DropzoneWithNotificationProvider = (props: Props) => (
  <ThemeProvider theme={theme}>
    <NotificationProvider>
      <Dropzone {...props} />
    </NotificationProvider>
  </ThemeProvider>
);

export default DropzoneWithNotificationProvider;
